import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Divider, Flex } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  ActionButton,
  Modal,
  Padding,
  SidePanel,
  Stack,
} from '../../common/components';
import {
  Form,
  FormCheckbox,
  FormField,
  FormInput,
  FormRadioGroup,
  FormSelect,
} from '../../common/form';
import { objectWithRandomUniqueKey } from '../../common/utils/randomUniqueKey';
import { DataSourceFormDataType } from '../schema';
import translations from '../translations/DataSourceDrawer.translations.json';
import { DataSourceFormDataSchema, sourceTypes } from '../types';
import { ConnectionField } from './ConnectionField';
import {
  useSystemClassifierItem,
  useSystemClassifierSelectOptions,
} from '../../configuration';

const { addTranslations, t, useTranslation } = i18nNsFactory(
  'data-object-source-drawer',
);
addTranslations(translations);

export interface DataSourceDrawerProps {
  closeDrawer: () => void;
  index?: number;
  isGrouped: boolean;
  setSource: (source: DataSourceFormDataType, index?: number) => void;
  source?: DataSourceFormDataType;
  sourcesCount: number;
}

export const createDefaultDataSource = (
  sourceType: any = null,
): DataSourceFormDataType =>
  objectWithRandomUniqueKey({
    id: null,
    code: '',
    name: '',
    description: null,
    sourceType,
    groupSource: false,
    dataLocation: '',
    connectionId: '',
    valueDateColumnName: null,
    dataObjectCodeColumnName: null,
    subjectCodeColumnName: null,
    contentType: null,
    contentBody: null,
  });

function DatabaseSourceFields() {
  const { getValues, setValue } = useFormContext();
  const isGrouped = useWatch({ name: 'groupSource' });

  const updateNameAndCode = useCallback(() => {
    const [dataLocation, code, name] = getValues([
      'dataLocation',
      'code',
      'name',
    ]);
    if (dataLocation && !code) {
      setValue('code', dataLocation, { shouldDirty: true });
    }
    if (dataLocation && !name) {
      setValue('name', dataLocation, { shouldDirty: true });
    }
  }, [getValues, setValue]);

  return (
    <>
      <Flex gap="large" vertical>
        <FormField
          name="dataLocation"
          label={t('fields.table.label')}
          onBlur={updateNameAndCode}
          placeholder={t('fields.table.placeholder')}
          required
        />
        <ConnectionField onBlur={updateNameAndCode} />
      </Flex>
      <Divider />
      <Flex gap="large" vertical>
        {isGrouped && (
          <FormField
            name="dataObjectCodeColumnName"
            label={t('fields.dataObjectCodeColumnName.label')}
            placeholder={t('fields.dataObjectCodeColumnName.placeholder')}
            required
          />
        )}
        <FormField
          name="valueDateColumnName"
          label={t('fields.valueDateColumnName.label')}
          placeholder={t('fields.valueDateColumnName.placeholder')}
          required
        />
        <FormField
          name="subjectCodeColumnName"
          label={t('fields.subjectCodeColumnName.label')}
          placeholder={t('fields.subjectCodeColumnName.placeholder')}
          required
        />
        <FormField
          name="code"
          label={t('fields.code.label')}
          placeholder={t('fields.code.placeholder')}
          required
        />
        <FormField
          name="name"
          label={t('fields.name.label')}
          placeholder={t('fields.name.placeholder')}
          required
        />
        <FormField name="description" label={t('fields.description.label')}>
          <FormInput name="description" rows={3} />
        </FormField>
      </Flex>
    </>
  );
}

function LocationSourceFields({ sourceType }: { sourceType: 'FILE' | 'URL' }) {
  const methodNameOptions = useSystemClassifierSelectOptions([
    'QUERY_METHOD_NAME',
  ])['QUERY_METHOD_NAME'];
  const methodNameId = useWatch({ name: 'methodNameId' });
  const postItem = useSystemClassifierItem('QUERY_METHOD_NAME', 'POST');
  const isPost = methodNameId === postItem?.id;
  const contentTypeOptions = useMemo(() => {
    if (!postItem?.parameters) return [];
    try {
      const values: string[] = JSON.parse(postItem.parameters);
      if (!Array.isArray(values)) {
        console.error(
          'Expected QUERY_METHOD_NAME/POST parameters to be an array. Instead got:',
          values,
        );
        return [];
      }
      return values.map((value) => ({ value, label: value }));
    } catch {
      console.error(
        'Failed to parse content-type options from QUERY_METHOD_NAME/POST parameters',
      );
      return [];
    }
  }, [postItem]);

  return (
    <>
      <Flex gap="large" vertical>
        <FormField
          name="dataLocation"
          label={t(`fields.${sourceType}.label`)}
          placeholder={t(`fields.${sourceType}.placeholder`)}
          required
        />
        {sourceType === 'URL' && (
          <>
            <FormField
              name="methodNameId"
              label={t('fields.methodNameId.label')}
              required
            >
              <FormSelect name="methodNameId" options={methodNameOptions} />
            </FormField>
            {isPost && (
              <>
                <FormField
                  name="contentType"
                  label={t('fields.contentType.label')}
                  required
                >
                  <FormSelect name="contentType" options={contentTypeOptions} />
                </FormField>
                <FormField
                  name="contentBody"
                  label={t('fields.contentBody.label')}
                  required
                >
                  <FormInput name="contentBody" rows={8} />
                </FormField>
              </>
            )}
          </>
        )}
        <ConnectionField />
      </Flex>
      <Divider />
      <FormField name="description" label={t('fields.description.label')}>
        <FormInput name="description" rows={3} />
      </FormField>
    </>
  );
}

function SourceFields() {
  const sourceType = useWatch({ name: 'sourceType' });

  if (!sourceType) {
    return null;
  }

  if (sourceType === 'DATABASE') {
    return <DatabaseSourceFields />;
  }
  return <LocationSourceFields sourceType={sourceType} />;
}

export const DataSourceDrawer: FC<DataSourceDrawerProps> = ({
  closeDrawer,
  index,
  isGrouped,
  source,
  setSource,
  sourcesCount,
}) => {
  const { t } = useTranslation();

  const submit = useCallback(
    (formData: DataSourceFormDataType) => {
      if (
        formData.groupSource &&
        (sourcesCount > 1 || (sourcesCount === 1 && index === undefined))
      ) {
        Modal.confirm({
          title: t('drawer.confirm.title'),
          okText: t('button.remove'),
          cancelText: t('button.cancel'),
          content: t('drawer.confirm.text'),
          onOk: () => setSource(formData, index),
        });
      } else {
        setSource(formData, index);
      }
    },
    [index, setSource, sourcesCount, t],
  );
  const canChangeSource = useMemo(() => {
    return sourcesCount === 0 || (sourcesCount === 1 && index !== undefined);
  }, [sourcesCount, index]);

  const sourceTypeOptions = useMemo(
    () =>
      (canChangeSource ? sourceTypes : ['DATABASE']).map((value) => ({
        value,
        label: t(`sourceType.${value}`),
      })),
    [t, canChangeSource],
  );

  return (
    <SidePanel
      side="right"
      width={1786}
      minWidth={450}
      collapsedWidth={450}
      mode="modal"
    >
      <SidePanelWrapper vertical>
        <Padding left right top>
          <h3>{t('drawer.title.new')}</h3>
        </Padding>
        <Form
          defaultValues={source as DataSourceFormDataType}
          schema={DataSourceFormDataSchema}
          onSubmit={submit}
          idAttributePrefix={`source-${
            typeof index === 'undefined' ? 'create' : index
          }`}
        >
          <FlexWrapper vertical>
            <BodyWrapper left right>
              <Divider orientation="left">{t('section.type')}</Divider>
              <FormField
                name="sourceType"
                aria-readonly={true}
                required
                label={t('sourceType.title')}
              >
                <FormRadioGroup
                  disabled={!canChangeSource}
                  name="sourceType"
                  options={sourceTypeOptions}
                />
              </FormField>
              {isGrouped && (
                <Padding top>
                  <FormField
                    name="groupSource"
                    label={t('fields.groupSource.label')}
                  >
                    <FormCheckbox name="groupSource" />
                  </FormField>
                </Padding>
              )}
              <Divider orientation="left">{t('section.settings')}</Divider>
              <SourceFields />
            </BodyWrapper>

            <ButtonWrapper>
              <Stack direction={'horizontal'} size={'sm'} justify={'end'}>
                <ActionButton type="abort" onClick={() => closeDrawer()} />
                <ActionButton label={t('button.ok')} type="formSubmit" />
              </Stack>
            </ButtonWrapper>
          </FlexWrapper>
        </Form>
      </SidePanelWrapper>
    </SidePanel>
  );
};

const SidePanelWrapper = styled(Flex)`
  border-left: 1px solid ${token.colorBorderSecondary};
  height: 100%;
  width: 100%;
`;

const FlexWrapper = styled(Flex)`
  width: 100%;
  height: calc(100% - 64px);
`;

const BodyWrapper = styled(Padding)`
  flex: 1 1 auto;
  overflow-y: auto;
`;

const ButtonWrapper = styled.div`
  border-top: 1px solid ${token.colorBorderSecondary};
  padding: 14px 12px 13px 12px;
  width: 100%;
`;
